import { Box, Button, CardContent, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { AlertTriangle, Package } from 'lucide-react';
import { AttentionItem } from '../../Types/vending';
import { COLORS } from '../../Styles/Colors';
import { VendingSectionCard, VendingSectionTitle } from '../../Components/Vending/VendingStyles';

const SEVERITY_CONFIG = {
  critical: {
    label: 'Critical',
    icon: <AlertTriangle size={16} />,
    color: COLORS.red,
    bg: `${COLORS.red}08`,
  },
  restock: {
    label: 'Restock',
    icon: <Package size={16} />,
    color: COLORS.orange,
    bg: `${COLORS.orange}0a`,
  },
};

interface AttentionCardProps {
  item: AttentionItem;
}

export const AttentionCard = ({ item }: AttentionCardProps) => {
  const config = SEVERITY_CONFIG[item.severity];

  return (
    <Box sx={{ mb: 2 }}>
      <VendingSectionCard style={{ backgroundColor: config.bg }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${config.color}14`,
                    color: config.color,
                    flexShrink: 0,
                  }}
                >
                  {config.icon}
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: COLORS.darkGray }}>
                  <Typography component="span" variant="subtitle2" fontWeight={700} sx={{ color: config.color, mr: 1 }}>
                    {config.label}
                  </Typography>
                  {item.title}
                </Typography>
              </Box>
              {item.lines.map((line) => (
                <Typography key={line} variant="body2" sx={{ color: COLORS.lightGray, mb: 0.25, pl: { sm: 4.5 } }}>
                  {line}
                </Typography>
              ))}
            </Box>
            <Button
              component={Link}
              to={item.actionPath}
              variant="outlined"
              size="small"
              sx={{
                flexShrink: 0,
                borderColor: `${COLORS.lightGray}80`,
                color: COLORS.teal,
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: COLORS.teal,
                  backgroundColor: `${COLORS.teal}0a`,
                },
              }}
            >
              {item.actionLabel}
            </Button>
          </Box>
        </CardContent>
      </VendingSectionCard>
    </Box>
  );
};

interface AttentionRequiredProps {
  items: AttentionItem[];
}

export const AttentionRequired = ({ items }: AttentionRequiredProps) => {
  if (items.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <VendingSectionCard>
          <CardContent>
            <VendingSectionTitle>Attention Required</VendingSectionTitle>
            <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
              No machines currently require immediate attention.
            </Typography>
          </CardContent>
        </VendingSectionCard>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <VendingSectionTitle>Attention Required</VendingSectionTitle>
      {items.map((item) => (
        <AttentionCard key={item.id} item={item} />
      ))}
    </Box>
  );
};

export default AttentionRequired;
